import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/base/Modal";
import { Button, confirmDelete, notifyError } from "../../../../components/ui";
import { usePatrolsStore } from "../../patrolsStore";
import { apiErrorMessage } from "../../services/basesApi";
import { CHECKPOINT_METHODS, type Checkpoint } from "../../services/patrolsApi";
import { DetailDrawer, DetailRow, FilterSelect, FormField, LoadBlock, Photo, SearchBox, SiteTree, Toolbar, inputCls } from "../../components/console";
import SiteMap from "../../components/SiteMap";
import { MethodBadge, treeMatch, useRondinesStatus, useRouteTree } from "./shared";

// Puntos de control: foto del punto, orden en su ruta, método de marcaje
// (QR, NFC o GPS), código de la etiqueta y coordenadas.

type Draft = Omit<Checkpoint, "id" | "base_id">;

export default function RondinesCheckpoints() {
  const { t } = useTranslation("bases");
  const { routes, checkpoints, saveCheckpoint, removeCheckpoint } = usePatrolsStore();
  const { status, error, retry } = useRondinesStatus();
  const tree = useRouteTree();
  const [sel, setSel] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [method, setMethod] = useState<string>("");
  const [open, setOpen] = useState<Checkpoint | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft; file: File | null } | null>(null);
  const [saving, setSaving] = useState(false);

  const routeName = (id: string) => routes.find((r) => r.id === id)?.name ?? "—";
  const routeColor = (id: string) => routes.find((r) => r.id === id)?.color ?? "#14b8a6";
  const list = checkpoints
    .filter((c) => treeMatch(sel, { base_id: c.base_id, route_id: c.route_id }) && (!method || c.method === method))
    .filter((c) => !q || `${c.name} ${c.code} ${c.zone}`.toLowerCase().includes(q.toLowerCase()));

  const newDraft = (): Draft => {
    const routeId = sel?.startsWith("route:") ? sel.slice(6) : routes[0]?.id ?? "";
    return { route_id: routeId, name: "", order: checkpoints.filter((c) => c.route_id === routeId).length + 1, method: "qr", code: "", latitude: null, longitude: null, radiusM: 25, instructions: "", zone: "", photo: null, required: true };
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.draft.name.trim() || !editing.draft.route_id) return void notifyError(t("rondines.checkpoints.required"));
    setSaving(true);
    try {
      const saved = await saveCheckpoint(editing.id, editing.draft, editing.file);
      if (open?.id === saved.id) setOpen(saved);
      setEditing(null);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: Checkpoint) => {
    if (!(await confirmDelete({ title: t("rondines.checkpoints.deleteTitle", { name: c.name }), cancelText: t("common.cancel"), confirmText: t("common.delete") }))) return;
    try {
      await removeCheckpoint(c.id);
      setOpen(null);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    }
  };

  const patch = (p: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...p } } : e));
  const num = (v: string) => (v === "" ? null : Number(v));

  return (
    <div className="flex h-full w-full overflow-hidden">
      <SiteTree title={t("rondines.treeTitle")} allLabel={t("rondines.allRoutes")} nodes={tree} selected={sel} onSelect={setSel} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Toolbar>
          <div className="mr-2 flex items-center gap-2">
            <i className="ri-map-pin-line text-lg text-primary-500" />
            <h1 className="text-sm font-semibold text-content">{t("rondines.checkpoints.title")}</h1>
            <span className="text-xs text-content-muted">({list.length})</span>
          </div>
          <SearchBox value={q} onChange={setQ} placeholder={t("rondines.checkpoints.search")} />
          <FilterSelect label={t("rondines.checkpoints.method")} value={method} onChange={setMethod} options={CHECKPOINT_METHODS.map((m) => ({ value: m, label: t(`rondines.method.${m}`) }))} />
          <div className="ml-auto">
            <Button size="sm" icon={<i className="ri-add-line" />} disabled={!routes.length} onClick={() => setEditing({ id: null, draft: newDraft(), file: null })}>
              {t("rondines.checkpoints.new")}
            </Button>
          </div>
        </Toolbar>
        <div className="flex-1 overflow-y-auto p-4">
          <LoadBlock status={status} error={error} onRetry={retry} empty={checkpoints.length === 0} emptyIcon="ri-map-pin-add-line" emptyTitle={t("rondines.checkpoints.emptyTitle")} emptyText={t("rondines.checkpoints.emptyText")}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {list.map((c) => (
                <button key={c.id} type="button" onClick={() => setOpen(c)} className="overflow-hidden rounded-xl border border-line bg-card text-left transition-shadow hover:shadow-lg">
                  <div className="relative">
                    <Photo src={c.photo} alt={c.name} thumb className="aspect-[16/9] w-full" icon="ri-map-pin-line" />
                    <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white ring-2 ring-white" style={{ background: routeColor(c.route_id) }}>
                      {c.order}
                    </span>
                    <span className="absolute right-2 top-2">
                      <MethodBadge method={c.method} />
                    </span>
                  </div>
                  <div className="space-y-0.5 p-3">
                    <div className="truncate text-sm font-semibold text-content">{c.name}</div>
                    <div className="truncate text-xs text-content-secondary">{routeName(c.route_id)}</div>
                    <div className="flex justify-between pt-1 text-[11px] text-content-muted">
                      <span className="font-mono">{c.code}</span>
                      <span>{c.zone}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </LoadBlock>
        </div>
      </div>

      <DetailDrawer
        open={Boolean(open)}
        onClose={() => setOpen(null)}
        title={open?.name}
        subtitle={open ? routeName(open.route_id) : null}
        actions={
          open ? (
            <>
              <Button size="sm" variant="soft" icon={<i className="ri-edit-line" />} onClick={() => setEditing({ id: open.id, draft: { ...open }, file: null })}>
                {t("common.edit")}
              </Button>
              <Button size="sm" variant="dangerSoft" iconOnly icon={<i className="ri-delete-bin-line" />} aria-label={t("common.delete")} onClick={() => void remove(open)} />
            </>
          ) : null
        }
      >
        {open ? (
          <div className="space-y-4">
            <Photo src={open.photo} alt={open.name} className="aspect-[16/10] w-full rounded-xl" icon="ri-map-pin-line" />
            <div className="divide-y divide-line rounded-xl border border-line bg-card px-3">
              <DetailRow icon="ri-hashtag" label={t("rondines.checkpoints.order")}>{open.order}</DetailRow>
              <DetailRow icon="ri-qr-code-line" label={t("rondines.checkpoints.method")}>
                <MethodBadge method={open.method} /> <span className="ml-2 font-mono text-xs">{open.code}</span>
              </DetailRow>
              <DetailRow icon="ri-map-pin-line" label={t("rondines.checkpoints.coords")}>
                {typeof open.latitude === "number" ? `${open.latitude.toFixed(5)}, ${open.longitude?.toFixed(5)} · ${open.radiusM ?? 25} m` : "—"}
              </DetailRow>
              <DetailRow icon="ri-shape-line" label={t("rondines.checkpoints.zone")}>{open.zone || "—"}</DetailRow>
              <DetailRow icon="ri-file-list-3-line" label={t("rondines.checkpoints.instructions")}>{open.instructions || "—"}</DetailRow>
            </div>
            {typeof open.latitude === "number" && typeof open.longitude === "number" ? (
              <div className="overflow-hidden rounded-xl border border-line">
                <SiteMap points={[{ id: open.id, lat: open.latitude, lng: open.longitude, label: open.name, order: open.order, color: routeColor(open.route_id) }]} className="h-44 w-full" />
              </div>
            ) : null}
          </div>
        ) : null}
      </DetailDrawer>

      {editing ? (
        <Modal
          title={editing.id ? t("rondines.checkpoints.edit") : t("rondines.checkpoints.new")}
          onClose={() => setEditing(null)}
          content={
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <FormField label={t("fields.name")} className="sm:col-span-2">
                <input className={inputCls} value={editing.draft.name} onChange={(e) => patch({ name: e.target.value })} />
              </FormField>
              <FormField label={t("rondines.checkpoints.order")}>
                <input className={inputCls} type="number" min={1} value={editing.draft.order} onChange={(e) => patch({ order: Number(e.target.value || 1) })} />
              </FormField>
              <FormField label={t("fields.route")}>
                <select className={inputCls} value={editing.draft.route_id} onChange={(e) => patch({ route_id: e.target.value })}>
                  {routes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("rondines.checkpoints.method")}>
                <select className={inputCls} value={editing.draft.method} onChange={(e) => patch({ method: e.target.value as Draft["method"] })}>
                  {CHECKPOINT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {t(`rondines.method.${m}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("rondines.checkpoints.code")}>
                <input className={inputCls} placeholder={t("rondines.checkpoints.codeAuto")} value={editing.draft.code ?? ""} onChange={(e) => patch({ code: e.target.value.toUpperCase() })} />
              </FormField>
              <FormField label={t("fields.latitude")}>
                <input className={inputCls} type="number" step="0.000001" value={editing.draft.latitude ?? ""} onChange={(e) => patch({ latitude: num(e.target.value) })} />
              </FormField>
              <FormField label={t("fields.longitude")}>
                <input className={inputCls} type="number" step="0.000001" value={editing.draft.longitude ?? ""} onChange={(e) => patch({ longitude: num(e.target.value) })} />
              </FormField>
              <FormField label={t("rondines.checkpoints.radius")}>
                <input className={inputCls} type="number" min={5} value={editing.draft.radiusM ?? 25} onChange={(e) => patch({ radiusM: Number(e.target.value || 25) })} />
              </FormField>
              <FormField label={t("rondines.checkpoints.zone")}>
                <input className={inputCls} value={editing.draft.zone ?? ""} onChange={(e) => patch({ zone: e.target.value })} />
              </FormField>
              <FormField label={t("rondines.checkpoints.instructions")} className="sm:col-span-2">
                <input className={inputCls} value={editing.draft.instructions ?? ""} onChange={(e) => patch({ instructions: e.target.value })} />
              </FormField>
              <div className="flex items-center gap-3 sm:col-span-3">
                <Photo src={editing.draft.photo} alt={editing.draft.name} thumb className="h-16 w-24 rounded-lg" icon="ri-map-pin-line" />
                <label className="cursor-pointer rounded-lg border border-dashed border-primary-500/60 px-3 py-2 text-xs text-primary-500 hover:bg-primary-500/10">
                  <i className="ri-image-add-line" /> {editing.file ? editing.file.name : t("rondines.checkpoints.photo")}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setEditing((x) => (x ? { ...x, file: e.target.files?.[0] ?? null } : x))} />
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-3">
                <Button variant="secondary" onClick={() => setEditing(null)}>
                  {t("common.cancel")}
                </Button>
                <Button loading={saving} onClick={() => void save()} icon={<i className="ri-save-line" />}>
                  {t("common.save")}
                </Button>
              </div>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
